import { useCallback } from 'react';

import {
  KommoLeadFacetsQuery,
  KommoLeadListQuery,
  KommoLeadStageListQuery,
  useKommoLeadFacetsQuery,
  useKommoLeadListQuery,
  useKommoLeadStageListQuery,
} from 'core/graphql';

import {
  ILeadsFilters,
  LEAD_PAGE_SIZE,
  toLeadsQueryVariables,
} from './leadsModel';
import { useLeadPermissions } from './useLeadPermissions';

/** ТЗ 0084 — список лидов Kommo и этапы воронки. */

export type IKommoLeadRow = KommoLeadListQuery['kommoLeads']['items'][number];
export type IKommoStage = KommoLeadStageListQuery['kommoLeadStages'][number];

// Пустые значения — константы модуля: свежий `[]` на каждый рендер в
// зависимостях эффекта вызывающего крутил бы эффект без конца.
const EMPTY_ROWS: IKommoLeadRow[] = [];
const EMPTY_STAGES: IKommoStage[] = [];

export interface IUseKommoLeads {
  rows: IKommoLeadRow[];
  total: number;
  /** `kommo` | `fake` | `disabled`; `null` — ответа ещё нет. */
  source: string | null;
  syncedAt: string | null;
  loading: boolean;
  failed: boolean;
  refetch: () => void;
}

/**
 * Страница списка. Пока едет ответ на новые фильтры, держим прошлую
 * (`previousData`): иначе таблица мигала бы загрузкой, а поле фильтра теряло
 * бы фокус вместе с размонтированной строкой (грабли
 * `query-vars-change-unmounts-subtree`).
 */
export const useKommoLeads = (
  filters: ILeadsFilters,
  enabled = true,
  /**
   * Строк на страницу. Доске строки таблицы не нужны — ей хватает шапки
   * (источник, синхронизация, итог), поэтому она просит одну строку.
   */
  pageSize: number = LEAD_PAGE_SIZE,
): IUseKommoLeads => {
  const { canUse } = useLeadPermissions();
  const { data, previousData, loading, error, refetch } = useKommoLeadListQuery(
    {
      variables: toLeadsQueryVariables(filters, pageSize),
      skip: !canUse || !enabled,
      fetchPolicy: 'cache-and-network',
    },
  );
  const page = (data ?? previousData)?.kommoLeads;
  const reload = useCallback(() => {
    refetch()?.catch(() => undefined);
  }, [refetch]);
  return {
    rows: page?.items ?? EMPTY_ROWS,
    total: page?.total ?? 0,
    source: page?.source ?? null,
    syncedAt: page?.syncedAt ?? null,
    loading,
    failed: Boolean(error) && !page,
    refetch: reload,
  };
};

/** Этапы воронки «New leads» с числом открытых лидов — чипы фильтра. */
export const useKommoLeadStages = (enabled = true) => {
  const { canUse } = useLeadPermissions();
  const { data, loading, error, refetch } = useKommoLeadStageListQuery({
    skip: !canUse || !enabled,
    fetchPolicy: 'cache-and-network',
  });
  const reload = useCallback(() => {
    refetch()?.catch(() => undefined);
  }, [refetch]);
  return {
    stages: data?.kommoLeadStages ?? EMPTY_STAGES,
    loading: loading && !data,
    /** Этапы не пришли (а прошлых нет) — показать ошибку с «Повторить». */
    failed: Boolean(error) && !data,
    refetch: reload,
  };
};

export type ILeadFacets = KommoLeadFacetsQuery['kommoLeadFacets'];

/**
 * ТЗ 0086 — значения меток и этапов дофинансирования со счётчиками по ВСЕМУ
 * зеркалу: варианты фильтров. Считает сервер не по отфильтрованной ленте —
 * после выбора значения соседние не пропадают. Закрытых лидов в счётчиках
 * нет, пока не отмечено «Показать закрытые» (как в самом списке).
 */
export const useKommoLeadFacets = (includeClosed: boolean, enabled = true) => {
  const { canUse } = useLeadPermissions();
  const { data, previousData } = useKommoLeadFacetsQuery({
    variables: { includeClosed: includeClosed ? true : null },
    skip: !canUse || !enabled,
    fetchPolicy: 'cache-and-network',
  });
  return { facets: (data ?? previousData)?.kommoLeadFacets ?? null };
};
